import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import FilterModal from '../components/FilterModal';
import Pagination from '../components/Pagination';
import { projectsAPI, showConfirmModal } from '../services/api';
import { Edit, Pencil, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { usePersistedState } from '../hooks/usePersistedState';
import { useBoardAutoRefresh } from '../hooks/useBoardAutoRefresh';
import BoardControls from '../components/BoardControls';
import ScrollSyncWrapper from '../components/ScrollSyncWrapper';
import ColumnToggle from '../components/ColumnToggle';
import { useHiddenColumns } from '../hooks/useHiddenColumns';
import { useColumnWidths } from '../hooks/useColumnWidths';
import { useColumnOrder } from '../hooks/useColumnOrder';
import InvoiceSentDateCell from '../components/InvoiceSentDateCell';
import CopyButton from '../components/CopyButton';
import MapsLink from '../components/MapsLink';
import { formatNoteAuthor } from '../utils/formatNoteAuthor';
import { useFranchiseColors } from '../hooks/useFranchiseColors';
import { namesMatch } from '../utils/nameMatch';
import { formatNoteTimestamp, noteTimestamp } from '../utils/noteDate';
import { readResponseJSON } from '../utils/fetchSafe';
import { dedupeProjectsById } from '../utils/dedupeProjects';

// Columns the user can toggle on/off from the Columns dropdown.
// Project # and the Actions column are intentionally not togglable.
const TOGGLABLE_COLUMNS = [
  { key: 'customer', label: 'Customer' },
  { key: 'job_location', label: 'Job Location' },
  { key: 'pre_tax_total_value', label: 'Subtotal' },
  { key: 'profit_margin', label: 'Profit Margin' },
  { key: 'pnd_date', label: 'FNOL Date' },
  { key: 'start_date', label: 'Start Date' },
  { key: 'finish_date', label: 'Finish Date' },
  { key: 'office', label: 'Office' },
  { key: 'production', label: 'Production' },
  { key: 'job_type', label: 'Job Type' },
  { key: 'job_payment', label: 'Job Payment' },
  { key: 'last_note_action', label: 'Last Note/Action' },
  { key: 'invoice', label: 'Invoice' },
  { key: 'invoice_sent_date', label: 'Invoice Sent Date' },
  { key: 'paid', label: 'Paid' },
];

const DeadProjects = () => {
  const navigate = useNavigate();
  const { hasPermission, user } = useAuth();
  const { colorFor } = useFranchiseColors();
  const [projects, setProjects] = useState([]);
  const [searchTerm, setSearchTerm] = usePersistedState('restorepm:dead:search', '');
  const [entriesPerPage, setEntriesPerPage] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [noteModal, setNoteModal] = useState({ open: false, projectId: null });
  const [noteText, setNoteText] = useState('');
  const [noteUrgent, setNoteUrgent] = useState(false);
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  const emptyFilters = {
    fnol_date_from: '', fnol_date_to: '', job_status: '', job_type: '',
    subtotal_from: '', subtotal_to: '',
    estimator: '', office_contact: '', production_mgr: '', marketing_rep: '',
    payment_type: '', paid_in_full: '',
  };
  const [filters, setFilters] = usePersistedState('restorepm:dead:filters', { ...emptyFilters });
  const [activeFilters, setActiveFilters] = usePersistedState('restorepm:dead:activeFilters', { ...emptyFilters });
  const [employees, setEmployees] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const [sortColumn, setSortColumn] = usePersistedState('restorepm:dead:sortColumn', null);
  const [sortDirection, setSortDirection] = usePersistedState('restorepm:dead:sortDirection', 'asc');
  const cols = useHiddenColumns('dead', user?.email);
  useColumnWidths('dead', user?.email);
  const colOrder = useColumnOrder('dead', user?.email, [
    'customer', 'job_location', 'pre_tax_total_value', 'profit_margin',
    'pnd_date', 'start_date', 'finish_date', 'office', 'production',
    'job_type', 'job_payment', 'last_note_action', 'invoice',
    'invoice_sent_date', 'paid',
  ]);

  useEffect(() => {
    fetchProjects();
    fetchDropdownData();
  }, []);

  const fetchDropdownData = async () => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    try {
      const [empRes, typeRes] = await Promise.all([
        fetch(`${API_URL}/api/employees?active=true`).then(readResponseJSON),
        fetch(`${API_URL}/api/project-types`).then(readResponseJSON),
      ]);
      setEmployees(empRes.employees || []);
      setProjectTypes(typeRes.project_types || typeRes || []);
    } catch {}
  };

  const fetchProjects = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const data = await projectsAPI.getAll('dead');
      setProjects(dedupeProjectsById(data.projects || []));
    } catch { if (!silent) toast.error('Failed to fetch dead projects'); }
    finally { if (!silent) setLoading(false); }
  };

  // Auto-refetch when the user returns to the tab or refocuses the window
  // so the board can never show stale data after editing a project.
  useBoardAutoRefresh(fetchProjects);

  const handleDelete = async (e, projectId) => {
    e.stopPropagation();
    if (!(await showConfirmModal('Are you sure you want to delete this project?'))) return;
    try {
      await projectsAPI.delete(projectId);
      toast.success('Project deleted');
      fetchProjects();
    } catch { toast.error('Failed to delete project'); }
  };

  const handleNoteClick = (e, projectId) => {
    e.stopPropagation();
    setNoteModal({ open: true, projectId });
    setNoteText('');
    setNoteUrgent(false);
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    try {
      await projectsAPI.addNote(noteModal.projectId, { text: noteText.trim(), urgent: noteUrgent });
      toast.success('Note added');
      setNoteModal({ open: false, projectId: null });
      fetchProjects();
    } catch { toast.error('Failed to add note'); }
  };

  const getJobTypeColor = (jobType) => {
    const t = jobType?.toLowerCase();
    if (t === 'mold') return 'bg-[#32CD32]';
    if (t === 'water') return 'bg-[#00BFFF]';
    if (t === 'fire') return 'bg-[#DC143C]';
    if (t === 'cleaning') return 'bg-[#00CED1]';
    if (t === 'supplemental water') return 'bg-[#00CED1]';
    return 'bg-gray-400';
  };

  const getJobTypeBorderColor = (jobType) => {
    const t = jobType?.toLowerCase();
    if (t === 'mold') return 'border-l-[#32CD32]';
    if (t === 'water') return 'border-l-[#00BFFF]';
    if (t === 'fire') return 'border-l-[#DC143C]';
    if (t === 'cleaning') return 'border-l-[#00CED1]';
    if (t === 'supplemental water') return 'border-l-[#00CED1]';
    return 'border-l-gray-400';
  };

  const getJobTypeCellClass = (jobType) => {
    const t = (jobType || '').toLowerCase();
    if (t === 'reconstruction') return 'bg-[#FFF3CD] text-gray-800';
    if (t === 'pack out' || t === 'packout') return 'bg-[#9B7CB8] text-white';
    if (t === 'bad debt') return 'bg-[#E86B9A] text-white';
    if (t === 'storage') return 'bg-[#C8A2D8] text-gray-800';
    if (t === 'water') return 'bg-[#87CEEB] text-gray-800';
    if (t === 'mold') return 'bg-[#90EE90] text-gray-800';
    if (t === 'fire') return 'bg-[#F08080] text-white';
    if (t === 'cleaning') return 'bg-[#40E0D0] text-gray-800';
    if (t === 'duct cleaning') return 'bg-[#FFD700] text-gray-800';
    if (t === 'biohazard') return 'bg-[#DC143C] text-white';
    if (t === 'check-in') return 'bg-[#87CEEB] text-gray-800';
    if (t === 'in progress') return 'bg-[#DC143C] text-white';
    if (t === 'claim') return 'bg-[#FF69B4] text-white';
    if (t === 'supplemental water') return 'bg-[#5FBDCC] text-white';
    return 'bg-gray-100 text-gray-800';
  };

  const hasActiveFilters = Object.values(activeFilters).some((v) => v !== '');

  const filteredProjects = projects.filter((project) => {
    const f = activeFilters;
    if (f.job_status) {
      const selected = f.job_status.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
      if (selected.length && !selected.includes((project.job_status || '').toLowerCase())) return false;
    }
    if (f.job_type && !(project.job_type || '').toLowerCase().includes(f.job_type.toLowerCase())) return false;
    if (f.estimator && !namesMatch(f.estimator, project.estimator)) return false;
    if (f.office_contact && !namesMatch(f.office_contact, project.office)) return false;
    if (f.production_mgr && !namesMatch(f.production_mgr, project.production)) return false;
    if (f.payment_type && !(project.job_payment || '').toLowerCase().includes(f.payment_type.toLowerCase())) return false;
    if (f.paid_in_full && !(project.paid || '').toLowerCase().includes(f.paid_in_full.toLowerCase())) return false;
    if (f.fnol_date_from || f.fnol_date_to) {
      const fd = project.fnol_date || project.pnd_date;
      if (!fd) return false;
      try {
        const d = new Date(fd);
        if (f.fnol_date_from && d < new Date(f.fnol_date_from)) return false;
        if (f.fnol_date_to && d > new Date(f.fnol_date_to)) return false;
      } catch { return false; }
    }
    if (f.subtotal_from || f.subtotal_to) {
      const val = parseFloat(String(project.pre_tax_total_value || '0').replace(/[$,]/g, '')) || 0;
      if (f.subtotal_from) { const from = parseFloat(String(f.subtotal_from).replace(/[$,]/g, '')) || 0; if (val < from) return false; }
      if (f.subtotal_to) { const to = parseFloat(String(f.subtotal_to).replace(/[$,]/g, '')) || 0; if (val > to) return false; }
    }
    if (!searchTerm) return true;
    const haystack = [
      project.project_id, project.customer, project.customer_name,
      project.job_location, project.address, project.job_type, project.phone
    ].map(f => String(f || '').toLowerCase()).join(' ');
    const tokens = searchTerm.toLowerCase().split(/\s+/).filter(Boolean);
    return tokens.every(tok => haystack.includes(tok));
  });

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortValue = (val, column) => {
    if (['invoice', 'aging_days', 'pre_tax_total_value'].includes(column)) {
      const num = parseFloat(String(val).replace(/[$,]/g, ''));
      return isNaN(num) ? 0 : num;
    }
    if (['pnd_date', 'start_date', 'finish_date', 'fnol_date'].includes(column)) {
      if (!val || val === 'n/a') return 0;
      const d = new Date(val);
      return isNaN(d.getTime()) ? 0 : d.getTime();
    }
    return String(val || '').toLowerCase();
  };

  const renderSortIcon = (column) => (
    <span className={`ml-1 text-[10px] ${sortColumn === column ? 'text-gray-700' : 'text-gray-300'}`}>
      {sortColumn === column ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}
    </span>
  );

  // Compute profit margin (%) for a project: (revenue - expenses) / revenue * 100.
  const getProfitMargin = (project) => {
    const revenue = parseFloat(String(project.pre_tax_total_value || '0').replace(/[$,]/g, '')) || 0;
    if (revenue <= 0) return null;
    const expenses = (project.expenses_list || []).reduce(
      (s, e) => s + (parseFloat(String(e.amount || '0').replace(/[$,]/g, '')) || 0),
      0
    );
    return ((revenue - expenses) / revenue) * 100;
  };

  const sortedProjects = [...filteredProjects].sort((a, b) => {
    if (!sortColumn) return 0;
    if (sortColumn === 'profit_margin') {
      const aVal = getProfitMargin(a);
      const bVal = getProfitMargin(b);
      const dir = sortDirection === 'asc' ? 1 : -1;
      const aNum = aVal === null ? -Infinity : aVal;
      const bNum = bVal === null ? -Infinity : bVal;
      if (aNum < bNum) return -1 * dir;
      if (aNum > bNum) return 1 * dir;
      return 0;
    }
    const aVal = getSortValue(a[sortColumn], sortColumn);
    const bVal = getSortValue(b[sortColumn], sortColumn);
    const dir = sortDirection === 'asc' ? 1 : -1;
    if (aVal < bVal) return -1 * dir;
    if (aVal > bVal) return 1 * dir;
    return 0;
  });

  const displayedProjects = sortedProjects.slice((currentPage - 1) * entriesPerPage, currentPage * entriesPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, entriesPerPage, activeFilters, sortColumn, sortDirection]);

  return (
    <Layout>
      <div className="p-4 lg:p-8">
        <div className="mb-6">
          <h2 className="text-base lg:text-lg text-[#5B8DB8] font-light" data-testid="dead-projects-subtitle">Projects</h2>
          <h1 className="text-2xl lg:text-3xl text-[#5B8DB8] font-light italic" data-testid="dead-projects-title">Dead</h1>
        </div>

        <div className="bg-white rounded shadow p-4 lg:p-6">
          <BoardControls
            filterButtons={
              <>
                <button
                  className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white px-5 py-2 rounded text-sm font-medium transition-colors"
                  data-testid="dead-edit-filters-btn"
                  onClick={() => { setFilters({ ...activeFilters }); setFilterModalOpen(true); }}
                >
                  Edit Filters
                </button>
                {hasActiveFilters && (
                  <button
                    className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-5 py-2 rounded text-sm font-medium transition-colors"
                    data-testid="dead-clear-filters-btn"
                    onClick={() => { setActiveFilters({ ...emptyFilters }); setFilters({ ...emptyFilters }); }}
                  >
                    Clear Filters
                  </button>
                )}
              </>
            }
            pinnedView={{
              board: 'dead',
              currentState: { filters, activeFilters, searchTerm, sortColumn, sortDirection },
              onApply: (s) => {
                if (s.filters) setFilters(s.filters);
                if (s.activeFilters) setActiveFilters(s.activeFilters);
                if (typeof s.searchTerm === 'string') setSearchTerm(s.searchTerm);
                if (s.sortColumn !== undefined) setSortColumn(s.sortColumn);
                if (s.sortDirection) setSortDirection(s.sortDirection);
              },
            }}
            columnToggle={
              <ColumnToggle
                columns={TOGGLABLE_COLUMNS}
                hidden={cols.hidden}
                onToggle={cols.toggle}
                onReset={cols.reset}
                onResetOrder={colOrder.reset}
                testIdSuffix="dead"
              />
            }
            entriesPerPage={entriesPerPage}
            setEntriesPerPage={setEntriesPerPage}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            entriesTestId="dead-entries-select"
            searchTestId="dead-search-input"
          />

          {/* Table */}
          <ScrollSyncWrapper>
            {loading ? (
              <div className="text-center py-8 text-gray-500" data-testid="loading-indicator">Loading dead projects...</div>
            ) : displayedProjects.length === 0 ? (
              <div className="text-center py-8 text-gray-500" data-testid="no-projects">No dead projects found</div>
            ) : (
              (() => {
                // Per-column metadata — same data-driven pattern as
                // AR / Open / Closed. See AccountsReceivable.jsx for
                // the rationale on why we render cells in the user's
                // `colOrder.order` instead of mutating DOM after render.
                // eslint-disable-next-line react/no-unstable-nested-components
                const colDefs = {
                  customer: {
                    th: () => <th className="col-customer text-left p-2 text-gray-600 font-normal text-xs cursor-pointer select-none" onClick={() => handleSort('customer')} data-testid="sort-customer" key="customer"><span className="inline-flex items-center">Customer{renderSortIcon('customer')}</span></th>,
                    td: (project) => (
                      <td className="col-customer p-2 align-top" key="customer">
                        <div className="text-xs whitespace-nowrap">
                          {project.customer_id ? (
                            <Link to={`/customers/${project.customer_id}`} className="text-[#5B8DB8] hover:text-[#3a6a91] hover:underline" data-testid={`customer-link-${project.project_id}`}>
                              {project.customer}
                            </Link>
                          ) : (
                            <span className="text-gray-700">{project.customer}</span>
                          )}
                        </div>
                        {project.phone && <div className="text-gray-400 text-xs">{project.phone}</div>}
                        {project.email && <div className="text-gray-400 text-xs">{project.email}</div>}
                      </td>
                    ),
                  },
                  job_location: {
                    th: () => <th className="col-job_location text-left p-2 text-gray-600 font-normal text-xs cursor-pointer select-none" onClick={() => handleSort('job_location')} data-testid="sort-job-location" key="job_location"><span className="inline-flex items-center">Job Location{renderSortIcon('job_location')}</span></th>,
                    td: (project) => {
                      const loc = project.address || project.job_location;
                      return (
                        <td className="col-job_location p-2 align-top" key="job_location">
                          <MapsLink address={loc} className="text-xs" testid={`job-location-link-${project.project_id}`} />
                        </td>
                      );
                    },
                  },
                  pre_tax_total_value: {
                    th: () => <th className="col-pre_tax_total_value text-left p-2 text-gray-600 font-normal text-xs cursor-pointer select-none" onClick={() => handleSort('pre_tax_total_value')} data-testid="sort-subtotal" key="pre_tax_total_value"><span className="inline-flex items-center">Subtotal{renderSortIcon('pre_tax_total_value')}</span></th>,
                    td: (project) => <td className="col-pre_tax_total_value p-2 text-gray-700 align-top text-xs whitespace-nowrap" key="pre_tax_total_value">{project.pre_tax_total_value ? `$${parseFloat(project.pre_tax_total_value).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : ''}</td>,
                  },
                  profit_margin: {
                    th: () => <th className="col-profit_margin text-left p-2 text-gray-600 font-normal text-xs cursor-pointer select-none" onClick={() => handleSort('profit_margin')} data-testid="sort-profit-margin" key="profit_margin"><span className="inline-flex items-center">Profit Margin{renderSortIcon('profit_margin')}</span></th>,
                    td: (project) => <td className="col-profit_margin p-2 text-gray-700 align-top text-xs whitespace-nowrap" key="profit_margin">{(() => { const m = getProfitMargin(project); return m === null ? '' : `${m.toFixed(2)}%`; })()}</td>,
                  },
                  pnd_date: {
                    th: () => <th className="col-pnd_date text-left p-2 text-gray-600 font-normal text-xs cursor-pointer select-none" onClick={() => handleSort('pnd_date')} data-testid="sort-pnd-date" key="pnd_date"><span className="inline-flex items-center">FNOL Date{renderSortIcon('pnd_date')}</span></th>,
                    td: (project) => <td className="col-pnd_date p-2 text-gray-700 align-top text-xs whitespace-nowrap" key="pnd_date">{project.fnol_date || project.pnd_date}</td>,
                  },
                  start_date: {
                    th: () => <th className="col-start_date text-left p-2 text-gray-600 font-normal text-xs cursor-pointer select-none" onClick={() => handleSort('start_date')} data-testid="sort-start-date" key="start_date"><span className="inline-flex items-center">Start Date{renderSortIcon('start_date')}</span></th>,
                    td: (project) => <td className="col-start_date p-2 text-gray-700 align-top text-xs whitespace-nowrap" key="start_date">{project.start_date || 'n/a'}</td>,
                  },
                  finish_date: {
                    th: () => <th className="col-finish_date text-left p-2 text-gray-600 font-normal text-xs cursor-pointer select-none" onClick={() => handleSort('finish_date')} data-testid="sort-finish-date" key="finish_date"><span className="inline-flex items-center">Finish Date{renderSortIcon('finish_date')}</span></th>,
                    td: (project) => <td className="col-finish_date p-2 text-gray-700 align-top text-xs whitespace-nowrap" key="finish_date">{project.finish_date || 'n/a'}</td>,
                  },
                  office: {
                    th: () => <th className="col-office text-left p-2 text-gray-600 font-normal text-xs cursor-pointer select-none" onClick={() => handleSort('office')} data-testid="sort-office" key="office"><span className="inline-flex items-center">Office{renderSortIcon('office')}</span></th>,
                    td: (project) => <td className="col-office p-2 text-gray-700 align-top text-xs" key="office">{project.office}</td>,
                  },
                  production: {
                    th: () => <th className="col-production text-left p-2 text-gray-600 font-normal text-xs cursor-pointer select-none" onClick={() => handleSort('production')} data-testid="sort-production" key="production"><span className="inline-flex items-center">Production{renderSortIcon('production')}</span></th>,
                    td: (project) => <td className="col-production p-2 text-gray-700 align-top text-xs" key="production">{project.production}</td>,
                  },
                  job_type: {
                    th: () => <th className="col-job_type text-left p-2 text-gray-600 font-normal text-xs cursor-pointer select-none" onClick={() => handleSort('job_type')} data-testid="sort-job-type" key="job_type"><span className="inline-flex items-center">Job Type{renderSortIcon('job_type')}</span></th>,
                    td: (project) => <td className={`col-job_type p-2 align-top text-xs ${getJobTypeCellClass(project.job_type)}`} key="job_type">{project.job_type}</td>,
                  },
                  job_payment: {
                    th: () => <th className="col-job_payment text-left p-2 text-gray-600 font-normal text-xs cursor-pointer select-none" onClick={() => handleSort('job_payment')} data-testid="sort-job-payment" key="job_payment"><span className="inline-flex items-center">Job Payment{renderSortIcon('job_payment')}</span></th>,
                    td: (project) => <td className="col-job_payment p-2 text-gray-700 align-top text-xs" key="job_payment">{project.job_payment}</td>,
                  },
                  last_note_action: {
                    th: () => <th className="col-last_note_action text-left p-2 text-gray-600 font-normal text-xs cursor-pointer select-none min-w-[280px]" onClick={() => handleSort('last_note_action')} data-testid="sort-last-note" key="last_note_action"><span className="inline-flex items-center">Last Note/Action{renderSortIcon('last_note_action')}</span></th>,
                    td: (project) => (
                      <td className="col-last_note_action p-2 align-top" key="last_note_action">
                        {(() => {
                          const notesList = project.notes_list || [];
                          if (notesList.length === 0) {
                            return (
                              <>
                                <div className="text-xs text-gray-700">{project.last_note_action}</div>
                                {project.notes && <div className="text-xs text-gray-500 max-w-[280px] line-clamp-3">{project.notes}</div>}
                              </>
                            );
                          }
                          const noteTs = (n) => noteTimestamp(n);
                          const recent = [...notesList].sort((a, b) => noteTs(b) - noteTs(a)).slice(0, 2);
                          return recent.map((note, nIdx) => {
                            const isUrgent = note.urgent;
                            const author = formatNoteAuthor(note.created_by);
                            return (
                              <div key={nIdx} className={`${nIdx > 0 ? 'mt-2 pt-2 border-t border-gray-100' : ''}`}>
                                <div className={`text-xs whitespace-nowrap ${isUrgent ? 'text-red-600' : 'text-gray-700'}`}>
                                  {formatNoteTimestamp(note)}
                                  {author && <span className="text-gray-400 ml-2">· {author}</span>}
                                </div>
                                <div title={note.text} className={`text-xs max-w-[280px] line-clamp-2 cursor-help ${isUrgent ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>{note.text}</div>
                              </div>
                            );
                          });
                        })()}
                      </td>
                    ),
                  },
                  invoice: {
                    th: () => <th className="col-invoice text-left p-2 text-gray-600 font-normal text-xs cursor-pointer select-none" onClick={() => handleSort('invoice')} data-testid="sort-invoice" key="invoice"><span className="inline-flex items-center">Invoice{renderSortIcon('invoice')}</span></th>,
                    td: (project) => <td className="col-invoice p-2 text-gray-700 align-top text-xs" key="invoice">{project.invoice}</td>,
                  },
                  invoice_sent_date: {
                    th: () => <th className="col-invoice_sent_date text-left p-2 text-gray-600 font-normal text-xs cursor-pointer select-none whitespace-nowrap" onClick={() => handleSort('invoice_sent_date')} data-testid="sort-invoice-sent" key="invoice_sent_date"><span className="inline-flex items-center">Invoice Sent Date{renderSortIcon('invoice_sent_date')}</span></th>,
                    td: (project) => (
                      <td className="col-invoice_sent_date p-2 align-top text-xs whitespace-nowrap" data-testid={`invoice-sent-${project.project_id}`} key="invoice_sent_date">
                        <InvoiceSentDateCell project={project} />
                      </td>
                    ),
                  },
                  paid: {
                    th: () => <th className="col-paid text-left p-2 text-gray-600 font-normal text-xs cursor-pointer select-none" onClick={() => handleSort('paid')} data-testid="sort-paid" key="paid"><span className="inline-flex items-center">Paid{renderSortIcon('paid')}</span></th>,
                    td: (project) => (
                      <td className="col-paid p-2 align-top text-xs" key="paid">
                        <span className={project.paid === 'Yes' ? 'text-green-600' : 'text-red-600'}>{project.paid}</span>
                      </td>
                    ),
                  },
                };
                const ordered = colOrder.order.filter((k) => colDefs[k]);
                Object.keys(colDefs).forEach((k) => { if (!ordered.includes(k)) ordered.push(k); });
                return (
              <table className="w-full text-sm min-w-[1400px] border-collapse" data-testid="dead-projects-table" data-board-table="dead">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="col-project_id text-left p-2 text-gray-600 font-normal text-xs cursor-pointer select-none" onClick={() => handleSort('project_id')} data-testid="sort-project-id"><span className="inline-flex items-center">Project #{renderSortIcon('project_id')}</span></th>
                    {ordered.map((key) => colDefs[key].th())}
                    <th className="p-2 sticky right-0 bg-white shadow-[-6px_0_8px_-2px_rgba(0,0,0,0.15)] z-10 border-l border-gray-200"></th>
                  </tr>
                </thead>
                <tbody>
                  {displayedProjects.map((project, index) => (
                    <tr
                      key={project._id || project.project_id}
                      data-testid={`dead-project-row-${project.project_id}`}
                      className={`border-b hover:bg-gray-50 transition-colors ${
                        index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                      }`}
                    >
                      <td
                        className="p-2 text-gray-700 align-top whitespace-nowrap text-xs pl-3"
                        style={{ borderLeft: `8px solid ${colorFor(project.territory)}` }}
                      >
                        <div className="flex flex-col items-start">
                          <span>{project.project_id}</span>
                          <CopyButton value={project.project_id} testid={`copy-project-id-${project.project_id}`} />
                        </div>
                      </td>
                      {ordered.map((key) => colDefs[key].td(project))}
                      <td className={`p-2 align-top sticky right-0 shadow-[-6px_0_8px_-2px_rgba(0,0,0,0.15)] z-10 min-w-[120px] border-l border-gray-200 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                        <div className="flex gap-1">
                          {hasPermission('can_update') && (
                          <a href={`/projects/${project._id || project.project_id}`} onClick={(e) => e.stopPropagation()} className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white rounded p-1.5 inline-flex items-center justify-center" data-testid={`edit-btn-${project.project_id}`}>
                            <Edit className="w-3.5 h-3.5" />
                          </a>
                          )}
                          <button onClick={(e) => handleNoteClick(e, project._id || project.project_id)} className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white rounded p-1.5" data-testid={`note-btn-${project.project_id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          {hasPermission('can_delete') && (
                          <button onClick={(e) => handleDelete(e, project._id || project.project_id)} className="bg-[#DC3545] hover:bg-[#C82333] text-white rounded p-1.5" data-testid={`delete-btn-${project.project_id}`}>
                            <X className="w-3.5 h-3.5" />
                          </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
                );
              })()
            )}
          </ScrollSyncWrapper>
          <Pagination
            total={sortedProjects.length}
            entriesPerPage={entriesPerPage}
            currentPage={currentPage}
            onChange={setCurrentPage}
            testIdPrefix="dead-pagination"
          />
        </div>
      </div>

      {/* Note Modal */}
      {noteModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-16 z-50" onMouseDown={(e) => { if (e.target === e.currentTarget) setNoteModal({ open: false, projectId: null }); }}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()} data-testid="note-modal">
            <textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Enter a project note" className="w-full border border-gray-300 rounded p-3 text-sm resize-y min-h-[160px] mb-4" data-testid="note-textarea" autoFocus />
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input type="checkbox" checked={noteUrgent} onChange={(e) => setNoteUrgent(e.target.checked)} className="w-4 h-4" data-testid="note-urgent-checkbox" />
                Mark as Urgent
              </label>
              <div className="flex gap-2">
                <button onClick={handleAddNote} className="bg-[#DC3545] hover:bg-[#C82333] text-white px-5 py-2 rounded text-sm font-medium transition-colors" data-testid="add-note-btn">Add Note</button>
                <button onClick={() => setNoteModal({ open: false, projectId: null })} className="bg-[#6C7B8A] hover:bg-[#5A6878] text-white px-5 py-2 rounded text-sm font-medium transition-colors" data-testid="close-note-btn">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Filters Modal */}
      <FilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        filters={filters}
        setFilters={setFilters}
        onUpdate={() => { setActiveFilters({ ...filters }); setFilterModalOpen(false); }}
        employees={employees}
        projectTypes={projectTypes}
        fields={[
          { type: 'date_range', label: 'FNOL Date', fromKey: 'fnol_date_from', toKey: 'fnol_date_to' },
          { type: 'select', label: 'Job Status', key: 'job_status' },
          { type: 'select', label: 'Job Type', key: 'job_type' },
          { type: 'number_range', label: 'Subtotal', fromKey: 'subtotal_from', toKey: 'subtotal_to' },
          { type: 'select', label: 'Estimator', key: 'estimator' },
          { type: 'select', label: 'Office Contact', key: 'office_contact' },
          { type: 'select', label: 'Production Mgr', key: 'production_mgr' },
          { type: 'select', label: 'Marketing Rep', key: 'marketing_rep' },
          { type: 'select', label: 'Payment Type', key: 'payment_type' },
          { type: 'select', label: 'Paid in Full', key: 'paid_in_full' },
        ]}
      />
    </Layout>
  );
};

export default DeadProjects;
